import { db, firebase } from "./index";

export const AdminRoles = {
  superadmin: "superadmin",
  trainer: "trainer",
};

function mapDoc(doc) {
  return {
    ...doc.data(),
    id: doc.id,
  };
}

function sortByName(a, b) {
  return (a.name || "").localeCompare(b.name || "");
}

function normalizePhone(phone) {
  return (phone || "").trim();
}

function normalizeAuthUid(uid) {
  return (uid || "").trim();
}

function sanitizeRollerData(data) {
  return {
    name: data.name || "",
    phone: normalizePhone(data.phone),
    birthdate: data.birthdate || "",
    levelId: data.levelId || null,
    playlist: data.playlist || "",
    trainerId: data.trainerId || null,
    updatedAt: Date.now(),
  };
}

function buildTrainerAdminUser(trainerId, trainer, existingAdminUser) {
  const now = Date.now();

  return {
    phone: trainer.phone,
    role: AdminRoles.trainer,
    trainerId,
    active: !!trainer.active,
    createdAt: existingAdminUser ? existingAdminUser.createdAt : now,
    updatedAt: now,
  };
}

export function getCurrentFirebaseUser() {
  return firebase.auth().currentUser;
}

export function onAdminAuthStateChanged(callback) {
  return firebase.auth().onAuthStateChanged(callback);
}

export async function logout() {
  await firebase.auth().signOut();
}

export async function getAdminUserByUid(uid) {
  if (!uid) {
    return null;
  }

  const doc = await db
    .collection("admin_users")
    .doc(uid)
    .get({ source: "server" });

  if (!doc.exists) {
    return null;
  }

  return mapDoc(doc);
}

export async function getAdminContext(firebaseUser) {
  if (!firebaseUser || !firebaseUser.uid || !firebaseUser.phoneNumber) {
    return null;
  }

  const adminUser = await getAdminUserByUid(firebaseUser.uid);

  if (!adminUser) {
    return null;
  }

  if (!adminUser.active) {
    return null;
  }

  if (adminUser.phone !== firebaseUser.phoneNumber) {
    return null;
  }

  let trainer = null;

  if (adminUser.role === AdminRoles.trainer && adminUser.trainerId) {
    const trainerDoc = await db
      .collection("trainers")
      .doc(adminUser.trainerId)
      .get();

    trainer = trainerDoc.exists ? mapDoc(trainerDoc) : null;
  }

  return {
    firebaseUser,
    adminUser,
    trainer,
  };
}

export async function listRollers(adminUser) {
  let records;

  if (adminUser.role === AdminRoles.superadmin) {
    records = await db.collection("rollers").get();
  } else {
    records = await db
      .collection("rollers")
      .where("trainerId", "==", adminUser.trainerId)
      .get();
  }

  return records.docs.map(mapDoc).sort(sortByName);
}

export async function listTrainers() {
  const records = await db.collection("trainers").get();

  return records.docs.map(mapDoc).sort(sortByName);
}

export async function listLevels() {
  const records = await db.collection("levels").get();

  return records.docs
    .map(mapDoc)
    .sort((a, b) => (a.difficulty || 0) - (b.difficulty || 0));
}

export async function createTrainer(data) {
  const now = Date.now();
  const phone = normalizePhone(data.phone);
  const authUid = normalizeAuthUid(data.authUid);

  if (!authUid) {
    throw new Error("Firebase Auth UID is required");
  }

  const trainerRef = db.collection("trainers").doc();
  const trainer = {
    name: data.name || "",
    phone,
    authUid,
    active: data.active !== false,
    createdAt: now,
    updatedAt: now,
  };

  const batch = db.batch();

  batch.set(trainerRef, trainer);
  batch.set(
    db.collection("admin_users").doc(authUid),
    buildTrainerAdminUser(trainerRef.id, trainer)
  );

  await batch.commit();

  return {
    ...trainer,
    id: trainerRef.id,
  };
}

export async function updateTrainer(id, data) {
  const trainerRef = db.collection("trainers").doc(id);
  const trainerDoc = await trainerRef.get();

  if (!trainerDoc.exists) {
    throw new Error("Trainer not found");
  }

  const currentTrainer = mapDoc(trainerDoc);
  const authUid = currentTrainer.authUid || normalizeAuthUid(data.authUid);

  if (!authUid) {
    throw new Error("Firebase Auth UID is required");
  }

  const trainer = {
    name: data.name || "",
    phone: currentTrainer.phone,
    authUid,
    active: data.active !== false,
    createdAt: currentTrainer.createdAt || Date.now(),
    updatedAt: Date.now(),
  };

  const adminUser = await getAdminUserByUid(authUid);
  const batch = db.batch();

  batch.update(trainerRef, trainer);
  batch.set(
    db.collection("admin_users").doc(authUid),
    buildTrainerAdminUser(id, trainer, adminUser)
  );

  await batch.commit();

  return {
    ...trainer,
    id,
  };
}

export async function updateRollerAdmin(id, data) {
  const roller = sanitizeRollerData(data);

  await db.collection("rollers").doc(id).update(roller);

  return {
    ...roller,
    id,
  };
}
